import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _findIndex from "lodash/findIndex";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}
/* eslint-disable react-hooks-ssr/react-hooks-global-ssr */

/* eslint-disable default-case */


import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { useMotionValue, motion } from 'framer-motion';
import { useIsomorphicLayoutEffect, useForkedRef, usePrevious } from "../../hooks";
import { InputAdornment } from "../TextField/InputAdornment";
import { Box } from "../Box";
import mergeRefs from 'react-merge-refs';
import { InputContainer } from "../TextField/TextField.styles";
import { theme } from "../../theme";
import { textStyles, truncateStyles } from "../Text";
import { Inline } from "../Inline";
import { eases, easeCurves } from "../../motion/eases";
import { StyledChip } from "../Chip/Chip";
import { getNextEnabledIndex, getPrevEnabledIndex } from "../../hooks/useDescendants/traversal-helpers";
import { DescendantsProvider } from "../../hooks/useDescendants";
import useResizeObserver from 'use-resize-observer';
import { Stack } from "../Stack";
import { TextFieldLabel } from "../TextField";
import { useId } from '@reach/auto-id';
var TRANSITION_DURATION = 0.25;
var VERTICAL_PADDING = 8;
var HALF_VERTICAL_PADDING = VERTICAL_PADDING / 2;
var INLINE_ANIMATIONS = {
  transition: {
    duration: TRANSITION_DURATION,
    ease: eases.primary
  },
  initial: {
    width: 'auto'
  },
  animate: {
    width: 'auto'
  },
  exit: {
    width: 0
  }
};
export var TokenizedField = React.forwardRef(function (_ref, forwardedRef) {
  var label = _ref.label,
      idProp = _ref.id,
      tokens = _ref.tokens,
      onTokenDelete = _ref.onTokenDelete,
      onTokenAdd = _ref.onTokenAdd,
      placeholder = _ref.placeholder,
      value = _ref.value,
      adornmentAfter = _ref.adornmentAfter,
      disabled = _ref.disabled,
      onKeyDownProp = _ref.onKeyDown,
      onFocusProp = _ref.onFocus,
      onBlurProp = _ref.onBlur,
      maxHeight = _ref.maxHeight,
      style = _ref.style,
      other = _objectWithoutProperties(_ref, ["label", "id", "tokens", "onTokenDelete", "onTokenAdd", "placeholder", "value", "adornmentAfter", "disabled", "onKeyDown", "onFocus", "onBlur", "maxHeight", "style"]);

  var id = useId(idProp);
  var inputContainerRef = useRef(null);
  var inputRef = useRef(null);
  var ref = useForkedRef(forwardedRef, inputRef);
  var valueContainerRef = useRef(null);
  var timerRef = useRef(null);

  var _useResizeObserver = useResizeObserver(),
      measureRef = _useResizeObserver.ref,
      _useResizeObserver$he = _useResizeObserver.height,
      height = _useResizeObserver$he === void 0 ? 0 : _useResizeObserver$he;

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      descendants = _useState2[0],
      setDescendants = _useState2[1];

  var shadowRef = useRef(null);
  var inputWidth = useMotionValue(0);

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      focused = _useState4[0],
      setFocused = _useState4[1];

  var tokenCount = React.Children.count(tokens);
  var prevTokenCount = usePrevious(tokenCount);
  var showPlaceholder = tokenCount === 0 && !value;
  var prevValue = usePrevious(value);
  useEffect(function () {
    return function () {
      return window.clearTimeout(timerRef.current);
    };
  }, []);

  function focusDescendant(i) {
    descendants[i].element.current.focus();
  }

  function focusInput() {
    inputRef.current.focus();
  }

  function getActiveIndex() {
    return _findIndex(descendants, function (d) {
      return d.element.current === document.activeElement;
    });
  }

  function handleKeyboardNav(e) {
    if (!tokenCount) {
      return;
    } // Handle instances where the user is focused in the input element


    if (document.activeElement === inputRef.current) {
      switch (e.key) {
        case 'Backspace':
        case 'ArrowLeft':
          {
            if (!value) {
              e.preventDefault();
              e.stopPropagation();
              focusDescendant(descendants.length - 1);
            }
          }
      }

      return;
    }

    var focusedIndex = getActiveIndex();

    switch (e.key) {
      case 'Backspace':
        {
          e.preventDefault();
          e.stopPropagation();
          var focusedDescendant = descendants[focusedIndex];

          if (!focusedDescendant) {
            return;
          }

          onTokenDelete(focusedIndex, focusedDescendant.id);

          if (tokenCount < 2) {
            // Focus the input container itself while animating out. This ensures
            // the input container maintains its ":focus" styles, even before
            // focus shifts back to the input element.
            inputContainerRef.current.focus(); // A bit of a hack to ensure that the cursor only appears
            // once the width animation finishes

            timerRef.current = setTimeout(function () {
              inputRef.current.focus();
            }, TRANSITION_DURATION * 1000 + 100);
          } else {
            var nextIndexToFocus = focusedIndex === descendants.length - 1 ? focusedIndex - 1 : focusedIndex + 1;

            if (timerRef.current) {
              clearTimeout(timerRef.current);
            } // This ensures that the input container maintains focus, and focus
            // styles until the next descedant is focused.


            inputContainerRef.current.focus();
            timerRef.current = setTimeout(function () {
              focusDescendant(focusedIndex > 0 ? nextIndexToFocus : 1);
            }, TRANSITION_DURATION * 1000);
          }

          return;
        }

      case 'ArrowLeft':
        {
          e.preventDefault();
          e.stopPropagation();
          var prevIndex = getPrevEnabledIndex(focusedIndex, descendants);
          focusDescendant(prevIndex);
          return;
        }

      case 'ArrowRight':
        {
          e.preventDefault();
          e.stopPropagation();

          if (focusedIndex === tokenCount - 1) {
            focusInput();
            return;
          }

          var nextIndex = getNextEnabledIndex(focusedIndex, descendants);
          focusDescendant(nextIndex);
        }
    }
  }

  function onKeyDown(e) {
    if (onKeyDownProp) {
      onKeyDownProp(e);
    }

    if (onTokenAdd && e.key === 'Enter' && value) {
      updateInputWidth('');
      onTokenAdd(value);
    }
  }

  function updateInputWidth(val) {
    var maxWidth = valueContainerRef.current.getBoundingClientRect().width; // Determine value width

    shadowRef.current.textContent = val;
    var measuredValueInputWidth = shadowRef.current.scrollWidth + 2;

    if (measuredValueInputWidth > maxWidth) {
      return;
    }

    inputWidth.set(measuredValueInputWidth);
  }

  useEffect(function () {
    if (prevTokenCount < tokenCount && maxHeight) {
      inputContainerRef.current.scrollTop = inputContainerRef.current.scrollHeight;
    }
  }, [maxHeight, prevTokenCount, tokenCount]);
  useIsomorphicLayoutEffect(function () {
    updateInputWidth(value);
  });

  function onMouseDown(e) {
    e.preventDefault();
    inputRef.current.focus();
  }

  function onFocus(e) {
    if (onFocusProp) {
      onFocusProp(e);
    }

    setFocused(true);
    updateInputWidth(value);
  }

  function onBlur(e) {
    if (onBlurProp) {
      onBlurProp(e);
    }

    updateInputWidth(value);
    setFocused(false);
  }

  return /*#__PURE__*/React.createElement(Stack, {
    gap: 8
  }, label && /*#__PURE__*/React.createElement(TextFieldLabel, {
    htmlFor: id
  }, label), /*#__PURE__*/React.createElement(StyledInputContainer, {
    as: motion.div,
    ref: inputContainerRef,
    role: "grid",
    $hasError: false,
    style: {
      height: height ? height + VERTICAL_PADDING : 'auto',
      // Only transition height when we aren't actively typing. This helps
      // prevent weird UI glitches when the input value jumps to the next line.
      transition: focused && value ? undefined : "all 0.2s ".concat(easeCurves.primary)
    },
    $maxHeight: maxHeight,
    disabled: disabled,
    onMouseDown: onMouseDown,
    onKeyDown: handleKeyboardNav,
    tabIndex: -1
  }, /*#__PURE__*/React.createElement(DescendantsProvider, {
    descendants: descendants,
    setDescendants: setDescendants
  }, /*#__PURE__*/React.createElement(StyledValueContainer, {
    $hasAdornmentAfter: !!adornmentAfter,
    ref: mergeRefs([valueContainerRef, measureRef])
  }, /*#__PURE__*/React.createElement(Box, {
    as: Inline,
    minWidth: 0,
    animate: INLINE_ANIMATIONS,
    gapX: 0,
    gapY: HALF_VERTICAL_PADDING
  }, React.Children.map(tokens, function (token, i) {
    return React.cloneElement(token, {
      role: 'row',
      clickable: true,
      onMouseDown: function onMouseDown(e) {
        return e.stopPropagation();
      },
      onDelete: function onDelete() {
        return onTokenDelete(i, descendants[i].id);
      }
    });
  }), /*#__PURE__*/React.createElement(SelectInput, null, /*#__PURE__*/React.createElement(Placeholder, {
    "aria-hidden": true,
    $animate: !(!prevValue && value),
    $isShowing: showPlaceholder
  }, placeholder), /*#__PURE__*/React.createElement(Input, _extends({
    ref: ref,
    style: _objectSpread(_objectSpread({}, style), {}, {
      width: inputWidth
    }),
    onKeyDown: onKeyDown,
    value: value,
    onFocus: onFocus,
    onBlur: onBlur,
    onMouseDown: function onMouseDown(e) {
      return e.stopPropagation();
    },
    "aria-label": label ? undefined : placeholder
  }, other)), /*#__PURE__*/React.createElement(InputShadow, {
    "aria-hidden": true,
    ref: shadowRef
  }))))), adornmentAfter && /*#__PURE__*/React.createElement(StyledAdornment, {
    onMouseDown: function onMouseDown(e) {
      return e.stopPropagation();
    },
    $placement: "end"
  }, adornmentAfter)));
});
var Placeholder = styled.div.withConfig({
  displayName: "TokenizedField__Placeholder",
  componentId: "vapor__n91suc-0"
})(["", ";", ";max-width:100%;position:absolute;line-height:", ";left:", ";color:", ";height:", ";opacity:", ";user-select:none;pointer-events:none;transition:", ";"], textStyles.primary, truncateStyles, theme.spaces[24], theme.spaces[8], theme.colors.palette.grey60, theme.spaces[24], function (p) {
  return p.$isShowing ? 1 : 0;
}, function (p) {
  return p.$animate ? 'opacity 0.2s ease' : 'none';
});
var StyledInputContainer = styled(InputContainer).withConfig({
  displayName: "TokenizedField__StyledInputContainer",
  componentId: "vapor__n91suc-1"
})(["cursor:text;align-items:flex-start;overflow:scroll;max-height:", ";outline:none;"], function (p) {
  return p.$maxHeight;
});
var StyledValueContainer = styled.div.withConfig({
  displayName: "TokenizedField__StyledValueContainer",
  componentId: "vapor__n91suc-2"
})(["display:flex;flex-wrap:wrap;position:relative;flex:1 1 0%;overflow:hidden;padding:", " ", ";min-height:", ";", " ", "{margin-right:", ";}"], theme.spaces[4], theme.spaces[8], theme.spaces[32], function (p) {
  return p.$hasAdornmentAfter && "\n    padding-right: 0;\n  ";
}, StyledChip, theme.spaces[8]);
var SelectInput = styled.div.withConfig({
  displayName: "TokenizedField__SelectInput",
  componentId: "vapor__n91suc-3"
})(["display:inline-block;"]);
var Input = styled(motion.input).withConfig({
  displayName: "TokenizedField__Input",
  componentId: "vapor__n91suc-4"
})(["background:0 center;box-sizing:border-box;border:0;font-size:inherit;opacity:1;outline:0;", ";line-height:", ";padding:", " 0;caret-color:", ";color:", ";&::placeholder{color:", ";}"], textStyles.primary, theme.spaces[16], theme.spaces[4], theme.colors.palette.brandPrimary, theme.colors.palette.tintA90, theme.colors.palette.tintA60);
var InputShadow = styled.div.withConfig({
  displayName: "TokenizedField__InputShadow",
  componentId: "vapor__n91suc-5"
})(["position:absolute;top:0;left:0;visibility:hidden;height:0;width:0;padding:0;overflow:scroll;white-space:pre;pointer-events:none;", ";line-height:", ";"], textStyles.primary, theme.spaces[16]);
var StyledAdornment = styled(InputAdornment).withConfig({
  displayName: "TokenizedField__StyledAdornment",
  componentId: "vapor__n91suc-6"
})(["position:sticky;top:0;"]);